import { ReactComponent as PaymentNoti } from '@/assets/images/common/paymentNoti.svg';
import { ReactComponent as PrescriptionNoti } from '@/assets/images/common/prescriptionNoti.svg';
import GlobalAlertPrescriptionModified
  from '@/components/AlertPrescriptionModifiedModal/GlobalAlertPrescriptionModified';
import { FirebaseMessageActions } from '@/hooks/common/useFirebaseListener';
import { Notification } from '@/recoil/notification';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import theme from '@/styles/theme';

interface Props {
  notification: Notification;
  setNotification: React.Dispatch<Notification>;
}

type NotificationState = {
  Icon: React.ReactElement;
  title: string;
  body: string;
};

type PrescriptionRevisionState = null;

type FirebaseMessageResponse = {
  NEW_PRESCRIPTION: NotificationState;
  PAID_PRESCRIPTION: NotificationState;
  CANCEL_PRESCRIPTION: NotificationState;
  PHARMACIST_IS_ABSENT: null;
  PHARMACY_DELIVERY_MATCH_FAILED: NotificationState;
  PRESCRIPTION_REVISION: PrescriptionRevisionState;
  PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST: NotificationState;
  NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT: NotificationState;
};

type NotificationActionState = {
  [K in FirebaseMessageActions]: FirebaseMessageResponse[K];
};

const NOTIFICATION_CLOSE_TIMEMAP: Record<FirebaseMessageActions, number> = {
  NEW_PRESCRIPTION: 360000,
  PAID_PRESCRIPTION: 5000,
  CANCEL_PRESCRIPTION: 5000,
  PHARMACIST_IS_ABSENT: 0,
  PHARMACY_DELIVERY_MATCH_FAILED: 360000,
  PRESCRIPTION_REVISION: 0,
  PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST: 5000,
  NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT: 5000
};

const NOTIFICATION_BUTTON_TEXT: Record<FirebaseMessageActions, string | null> =
  {
    NEW_PRESCRIPTION: '처방전 확인하기',
    PAID_PRESCRIPTION: '조제 시작하기',
    CANCEL_PRESCRIPTION: null,
    PHARMACIST_IS_ABSENT: null,
    PHARMACY_DELIVERY_MATCH_FAILED: '배송 접수하기',
    PRESCRIPTION_REVISION: null,
    PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST: '상세보기',
    NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT: '상세보기'
  };

const NotificationModal = (props: Props) => {
  const { notification, setNotification } = props;
  const history = useHistory();
  const notificationType = notification.recent?.action;
  const isPlayAlertSound = notificationType === 'NEW_PRESCRIPTION';

  const onReset = () => {
    setNotification({
      ...notification,
      recent: null
    });
  };

  const navigate = () => {
    if (notificationType === FirebaseMessageActions.NEW_PRESCRIPTION) {
      history.push('/requested');
      return;
    }
    if (
      notificationType === FirebaseMessageActions.PHARMACY_DELIVERY_MATCH_FAILED
    ) {
      history.push(`/preparing/${notification.recent?.orderId}`);
      return;
    }
    if (
      notificationType ===
      FirebaseMessageActions.PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST
    ) {
      history.push(`/preparing/${notification.recent?.orderId}`);
      return;
    }
    if (
      notificationType ===
      FirebaseMessageActions.NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT
    ) {
      history.push(`/preparing/${notification.recent?.orderId}`);
      return;
    }
    history.push('/preparing');
  };

  const goListPage = () => {
    onReset();
    navigate();
    window.location.reload(); // 이거 안해도 될 것 같은데 rq 이슈 잡을때 보기
  };

  const notificationList: NotificationActionState | PrescriptionRevisionState =
    {
      NEW_PRESCRIPTION: {
        Icon: <PrescriptionNoti />,
        title: '새로운 처방전이 접수 되었습니다',
        body: '10분 안에 조제 여부를 결정해주세요'
      },
      PAID_PRESCRIPTION: {
        Icon: <PaymentNoti />,
        title: '처방전 결제 완료되었습니다',
        body: '조제를 시작해주세요'
      },
      CANCEL_PRESCRIPTION: {
        Icon: <PrescriptionNoti />,
        title: '응답시간 초과로 접수가 취소되었습니다.',
        body: '처방전 접수 취소'
      },
      PHARMACIST_IS_ABSENT: null,
      PHARMACY_DELIVERY_MATCH_FAILED: {
        Icon: <PrescriptionNoti />,
        title: '배송 접수 실패 안내',
        body: '배송 접수 실패했습니다. 다시 배송 접수해주세요.'
      },
      PRESCRIPTION_REVISION: null,
      PICKUP_DELAYED_IMMEDIATE_PAYMENT_ORDER_TO_PHARMACIST: {
        Icon: <PrescriptionNoti />,
        title: notification.recent?.title ?? '',
        body: notification.recent?.body ?? ''
      },
      NEW_PRESCRIPTION_BY_IMMEDIATE_PAYMENT: {
        Icon: <PrescriptionNoti />,
        title: notification.recent?.title ?? '',
        body: notification.recent?.body ?? ''
      }
    };
  const audioPath = require('../../../assets/audio/waterdrop.m4a');

  useEffect(() => {
    if (
      notification.recent?.action ===
      FirebaseMessageActions.PRESCRIPTION_REVISION
    ) {
      GlobalAlertPrescriptionModified.show({
        title: notification.recent.title,
        body: notification.recent.body,
        orderId: notification.recent.orderId!,
        orderExternalId: notification.recent.orderExternalId!,
        isPaid: notification.recent.isPaid === 'false' ? false : true
      });
    }

    if (!notificationType) return;

    const actionItem = notificationList[notificationType];

    if (!actionItem) return;

    toast.dark(
      <ToastContent>
        <ContentTitle color={theme.DF_COLOR.WHITE}>
          {notification?.recent?.title ?? actionItem?.title}
        </ContentTitle>
        <ContentBody color={theme.DF_COLOR.GRAY100}>
          {notification?.recent?.body ?? actionItem?.body}
        </ContentBody>
        {NOTIFICATION_BUTTON_TEXT[notificationType] && (
          <PrescriptionCheckButton onClick={goListPage}>
            {NOTIFICATION_BUTTON_TEXT[notificationType]}
          </PrescriptionCheckButton>
        )}
      </ToastContent>,
      {
        onClick: goListPage,
        hideProgressBar: true,
        autoClose: NOTIFICATION_CLOSE_TIMEMAP[notificationType],
        toastId: isPlayAlertSound ? notification.recent?.orderId : undefined
      }
    );

    setTimeout(() => {
      onReset();
    }, NOTIFICATION_CLOSE_TIMEMAP[notificationType]);
  }, [notification.recent]);

  return (
    <Wrapper>
      {isPlayAlertSound && (
        <>
          <iframe
            src={require('../../../assets/audio/silence.mp3')}
            allow="autoplay"
            id="audio"
            style={{ display: 'none' }}
          />
          <audio autoPlay src={audioPath} />
        </>
      )}
    </Wrapper>
  );
};

export default NotificationModal;

const Wrapper = styled.div`
  text-align: center;
`;

const ContentTitle = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ContentBody = styled(ContentTitle)`
  font-size: 17px;
  font-weight: 400;
  white-space: pre-wrap;
`;

const PrescriptionCheckButton = styled.button`
  width: fit-content;
  color: ${theme.DF_COLOR.PRIMARY500};
  outline: none;
  border: none;
  text-decoration-line: underline;
  cursor: pointer;
  font-size: 17px;
  font-weight: 400;
`;

const ToastContent = styled.div`
  display: flex;
  flex-direction: column;
`;
